if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      if (event?.detail?.currentElement !== undefined) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();


      // This automatically scrolls to the first variant image on variant change
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product-modal-opener-image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  }
  customElements.define('media-gallery', MediaGallery)

  customElements.define('variant-media-gallery', class VariantMediaGallery extends MediaGallery {
    connectedCallback() {
      this.bindListeners()

      if (this.dataset.variantGalleries === 'true') {
        const currentVariantId = this.getCurrentVariantId()
        // Server already rendered 'for-variant'/is-active for this variant, so
        // this initial pass only needs to sync class state (idempotent) — it
        // must not move active media or announce the live region.
        if (currentVariantId) this.filterForVariant(currentVariantId, { initial: true })
      }

      // SALE: initial badge pass, for every product (not only variant-gallery ones).
      // Can't wait for a variantChange: the buy box only dispatches one at load for
      // subscribable products, and bundles pages (no buy box) dispatch none until a
      // click. Reads current state instead, so it's right in either order — if the
      // buy box already synced the form, the form holds its variant; if not, the
      // form still holds the server's (?variant=) one the gallery was rendered for.
      this.refreshSaleBadge()
    }

    bindListeners() {
      const variantRadios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`)
      if(variantRadios) {
        variantRadios.addEventListener('variantChange', this.onVariantChange.bind(this))
      }
    }

    /**
     * Reads the currently selected variant id from the add-to-cart form's
     * hidden id input, for use at connectedCallback time before any
     * variantChange event has fired.
     */
    getCurrentVariantId() {
      const form = document.getElementById(`product-form-${this.dataset.sectionId}`)
      const idInput = form?.querySelector('input[name="id"]')
      return idInput?.value
    }

    /**
     * True when the element carries the current variant's id in its
     * pipe-delimited data-variant-ids list (e.g. "|123|456|"). Delimited so a
     * variant id like 123 can't accidentally match 1234.
     */
    matchesVariant(element, needle) {
      return (
        typeof element.dataset.variantIds !== 'undefined' &&
        element.dataset.variantIds !== null &&
        element.dataset.variantIds.includes(needle)
      )
    }

    /**
     * when variant changes, iterate over slides
     * if the variantId data property contains the current variant ID, then show
     *
     * after, update media count for a11y
     */
    onVariantChange(e) {
      const variantId = e.detail.id
      if (variantId) this.filterForVariant(variantId)
      // SALE: both emitters (global.js VariantRadios and the buy box's
      // changeVariant) put the full variant in the detail — with any sale_demo
      // injection already applied — so no lookup is needed. A detail without an id
      // (no variant for the chosen options) clears the badge.
      this.updateSaleBadge(variantId ? e.detail : null)
    }

    /**
     * SALE: the variant object for an id, from this section's
     * #json-product-variants tag. Scoped to this section's <variant-radios> so a
     * second product on the page can't answer. Parsed on demand, not cached: in
     * sale_demo mode the buy box rewrites the tag with injected compare-at prices,
     * and a cached copy could predate that. Null when the tag is absent (e.g.
     * single-variant products, which don't render the picker).
     */
    getVariantById(variantId) {
      const radios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`)
      const json = radios?.querySelector('#json-product-variants')
      if (!json || !variantId) return null
      try {
        const id = Number(variantId)
        return JSON.parse(json.textContent).find(v => v.id === id) || null
      } catch (err) {
        return null
      }
    }

    /**
     * SALE: re-derives the badge from current state (the form's variant id). Used
     * at connect time and by the buy box after a sale_demo injection. Without
     * variant data it leaves the server-rendered state untouched.
     */
    refreshSaleBadge() {
      const variant = this.getVariantById(this.getCurrentVariantId())
      if (variant) this.updateSaleBadge(variant)
    }

    /**
     * SALE: shows the "On Sale" badge on exactly one main slide when the variant
     * is on sale (compare_at_price > price), hides it everywhere otherwise.
     *  - variant has its own gallery → the first slide of it (DOM order is the
     *    gallery order, so querySelector's first match is the first image);
     *  - otherwise → the slide for its featured_media;
     *  - neither rendered → no badge.
     * The badge spans are server-rendered in every main slide (never thumbnails
     * or the zoom modal), so this only toggles a class.
     *
     * Gated: sections/main-product.liquid renders the spans and
     * data-sale-badges="true" only when the product is in the Sale collection
     * (theme setting), the "Show On Sale badges" toggle is on and the badge text
     * isn't blank. Without that flag this never turns a badge on.
     */
    updateSaleBadge(variant) {
      if (this.dataset.saleBadges !== 'true') return
      const badges = this.querySelectorAll('.product-media-item .jl-gallery-sale-badge')
      if (!badges.length) return
      let target = null
      if (variant && variant.compare_at_price > variant.price) {
        target = this.querySelector(`.product-media-item[data-media-id*="-vg${variant.id}-"] .jl-gallery-sale-badge`)
        const featuredId = variant.featured_media?.id
        if (!target && featuredId) {
          target = this.querySelector(`.product-media-item[data-media-id="${this.dataset.sectionId}-${featuredId}"] .jl-gallery-sale-badge`)
        }
      }
      badges.forEach(badge => badge.classList.toggle('is-on', badge === target))
    }

    /**
     * Toggles 'for-variant' on the main slides for the given variant id. This
     * baseline pass always runs, matching pre-existing behavior for products
     * without per-variant galleries (where 'for-variant' has no visual effect).
     *
     * When data-variant-galleries="true" is set, also filters thumbnails and
     * modal entries, makes the variant's first visible image active, resets
     * slider pagination, and shows/hides the arrows and thumbnail slider.
     */
    filterForVariant(variantId, { initial = false } = {}) {
      const needle = `|${variantId}|`
      const slides = Array.from(this.querySelectorAll('.product-media-item'))

      let mediaCount = 0
      let firstVisibleMediaId = null

      slides.forEach(slide => {
        const matches = this.matchesVariant(slide, needle)
        slide.classList.toggle('for-variant', matches)
        if (matches) {
          mediaCount++
          if (!firstVisibleMediaId) firstVisibleMediaId = slide.dataset.mediaId
        }
      })

      if (this.dataset.variantGalleries !== 'true') return

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('.thumbnail-list-item').forEach(thumbnail => {
          thumbnail.classList.toggle('for-variant', this.matchesVariant(thumbnail, needle))
        })
      }

      const modalContent = document.querySelector(`#ProductModal-${this.dataset.sectionId} .product-media-modal-content`)
      if (modalContent) {
        modalContent.querySelectorAll('[data-variant-ids]').forEach(entry => {
          const matches = this.matchesVariant(entry, needle)
          entry.classList.toggle('for-variant', matches)
          entry.style.display = matches ? '' : 'none'
        })
      }

      // Reuse the inherited setActiveMedia() rather than hand-rolling active
      // state / thumbnail sync / live region announcement.
      if (!initial && firstVisibleMediaId) this.setActiveMedia(firstVisibleMediaId, false)

      if (this.elements.viewer?.resetPages) this.elements.viewer.resetPages()
      if (this.elements.thumbnails?.resetPages) this.elements.thumbnails.resetPages()

      this.toggleControls(mediaCount)
    }

    /**
     * Hides the thumbnail rail entirely when the variant has fewer than 2
     * visible images, restoring it otherwise. Also collapses the arrows
     * container's ('.slider-buttons') layout space (gap/padding) when
     * hidden, via the same 'vg-controls-hidden' class.
     *
     * NOTE: the arrows container never actually shows visible arrow
     * buttons for this viewer, with or without 'vg-controls-hidden' —
     * '.product-media-wrapper .slider-button { display: none }' in
     * assets/section-main-product.css hides the individual buttons
     * unconditionally (it's only overridden for
     * '.product-thumbnail-slider .thumbnail-slider .slider-button', which
     * this viewer's arrows are not inside), and
     * assets/component-slider.css also hides the whole '.slider-buttons'
     * container below 800px regardless. So toggling 'vg-controls-hidden'
     * here only ever removes/restores the (button-less) container's own
     * layout space — it never reveals arrow UI. There is no arrow UI to
     * build here; that's out of scope.
     *
     * The thumbnail rail is hidden the same way (rather than inline
     * style.display) because assets/theme.css hides '.thumbnail-slider'
     * below 800px via '.small-hide { display: none !important }' when the
     * "mobile_thumbnails" setting is 'hide' — an inline style can never win
     * against that. The '.vg-controls-hidden' selector is written with
     * higher specificity so it always wins instead.
     */
    toggleControls(mediaCount) {
      const hideArrows = mediaCount < 2 || this.dataset.mobileThumbnails === 'show'
      const arrows = this.elements.viewer?.querySelector('.slider-buttons')
      if (arrows) arrows.classList.toggle('vg-controls-hidden', hideArrows)
      if (this.elements.thumbnails) {
        this.elements.thumbnails.classList.toggle('vg-controls-hidden', mediaCount < 2)
      }
      // Collapses the desktop grid's thumbnail-rail column (see
      // component-variant-gallery.css) when the rail is hidden, so the
      // viewer doesn't leave behind an empty 6.5rem/7.5rem gutter.
      this.classList.toggle('vg-rail-hidden', mediaCount < 2)
    }
  });
}
